// LIBRERIE/IMPORTS
import React, { useRef, useState, useCallback, useEffect } from 'react';
import Slider from 'react-slick';
import { useTranslation } from "react-i18next";

// COMPONENTI FIGLI
import { NavigationTabs } from './components/NavigationTabs';
import { MainSlider } from './components/MainSlider';
import { ThumbnailSlider } from './components/ThumbnailSlider';
import { GalleryModal } from './components/GalleryModal';

// TIPI E HOOKS
import { ImageDetail } from './types';
import useValidImages from '../../../hooks/useValidImages';

interface PropertyGalleryProps {
    images: ImageDetail[];
    activeLogoOnPhotos: boolean;
    caption: string;
    hasPlanimetry: boolean;
}

export const PropertyGallery: React.FC<PropertyGalleryProps> = ({
    images,
    activeLogoOnPhotos,
    caption,
    hasPlanimetry
}) => {
    const { t } = useTranslation()
    const planimetryTabTextForNav = t('gallery.navigationTabPlanimetry');
    const mainGallerySliderRef = useRef<Slider>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const validImages = useValidImages(images);

    // GESTIONE GALLERIA IMMAGINI USARE NEL CAROSELLO (CHE SONO PROPS DA PASSARE)-------------------------------------
    const detailLogoImages = [] as {id:number, main:boolean, url:string|null}[];
    const detailImages = [] as {id:number, main:boolean, url:string|null}[];
    const thumbnailImages = [] as {id:number, main:boolean, url:string|null}[];

    if (validImages.length > 0 && activeLogoOnPhotos) {
        validImages.forEach(image => {
            if (image.urlLogo) {
                detailLogoImages.push(
                    {
                        id:image.id,
                        main: image.main,
                        url: image.urlLogo 
                    }
                )
            }else if(image.urlBig){
                detailLogoImages.push(
                    {
                        id:image.id,
                        main: image.main,
                        url: image.urlBig 
                    }
                )
            }
        })
    }else if(validImages.length > 0 && !activeLogoOnPhotos){
        validImages.forEach(image => {
            if (image.urlBig) {
                detailImages.push(
                    {
                        id:image.id,
                        main: image.main,
                        url: image.urlBig 
                    }
                )
                thumbnailImages.push(
                    {
                        id:image.id,
                        main: image.main,
                        url: image.urlSmall
                    }
                )
            }
        })
    }
    // Img definiteve da usare nel carosello
    const imagesToUse = detailLogoImages.length>0 ? detailLogoImages : detailImages;
    const thumbnailToUse = detailLogoImages.length>0 ? detailLogoImages : thumbnailImages;

    // ----------------------------------------------------------------------------------------

    // Memoization delle funzioni
    const scrollToPlanimetry = useCallback(() => {
        const element = document.getElementById('planimetrie');
        element?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const openModal = useCallback((index: number) => {
        setCurrentImageIndex(index);
        setModalIsOpen(true);
    }, []);

    const closeModal = useCallback(() => setModalIsOpen(false), []);

    const nextImage = useCallback(() => {
        if (imagesToUse.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % imagesToUse.length);
        }
    }, [imagesToUse.length]);

    const prevImage = useCallback(() => {
        if (imagesToUse.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + imagesToUse.length) % imagesToUse.length);
        }
    }, [imagesToUse.length]);

    useEffect(() => { setCurrentImageIndex(currentImageIndex); }, [imagesToUse]);
    const modalCloseLabel = t('gallery.modalCloseButtonLabel');
    const modalPrevLabel = t('gallery.modalPrevButtonLabel');
    const modalNextLabel = t('gallery.modalNextButtonLabel');
    const modalContentLabelText = t('gallery.modalContentLabel');
    const modalCounterPatternText = t('property.modalCounterPattern');

    return (
        <div id='galleria' className='property-detail-gallery overflow-hidden'>
            {hasPlanimetry && (
                <NavigationTabs scrollToPlanimetry={scrollToPlanimetry} planimetryTabText={planimetryTabTextForNav}/>
            )}

            {validImages.length > 0 && ( // Mostra slider solo se ci sono immagini
                <div className='slider-slick'>
                    <MainSlider
                        images={imagesToUse}
                        sliderRef={mainGallerySliderRef}
                        gallerySettings={{
                            dots: false,
                            infinite: true,
                            speed: 500,
                            slidesToShow: 1,
                            slidesToScroll: 1,
                            adaptiveHeight: true
                        }}
                        onImageClick={openModal}
                    />

                    <ThumbnailSlider
                        images={thumbnailToUse}
                        onThumbnailClick={(index) => mainGallerySliderRef.current?.slickGoTo(index)}
                    />
                </div>
            )}

            {validImages.length > 0 && ( // Mostra la modale solo se ci sono immagini valide
                <GalleryModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    currentImage={imagesToUse[currentImageIndex] || imagesToUse[0]}
                    currentIndex={currentImageIndex}
                    totalImages={imagesToUse.length}
                    onPrev={prevImage}
                    onNext={nextImage}
                    caption={caption}
                    closeButtonLabel={modalCloseLabel}
                    prevButtonLabel={modalPrevLabel}
                    nextButtonLabel={modalNextLabel}
                    counterPattern={modalCounterPatternText}
                    contentLabel={modalContentLabelText}
                />
            )}
        </div>
    );
};

export default PropertyGallery;